import { notify } from "@general-backoffice/core";
import type { User } from "@investhinks/api-client";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";

import { referrerEndpoints } from "../../api";
import getErrorMessage from "../../utils/getErrorMessage";
import UserSelect from "./UserSelect";

interface AssignReferrerModalProps {
  isOpen: boolean;
  toggle: () => void;
  user: User | null;
  referrer: User | null;
  onSuccess?: () => void;
}

const AssignReferrerModal: React.FC<AssignReferrerModalProps> = ({ isOpen, toggle, user, referrer, onSuccess }) => {
  const { t } = useTranslation("users", { keyPrefix: "assignReferrer" });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);

  const userId = user?.id;
  const displayName = user?.name || user?.email || `User #${userId}`;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedUser(null);
      setError(null);
      setShowRemoveConfirm(false);
      setShowReplaceConfirm(false);
    }
  }, [isOpen]);

  // Replacing an existing referrer reroutes future commission from one person
  // to another, so it is confirmed the way removal already was.
  const isReplacing = !!referrer && !!selectedUser && `${referrer.id}` !== `${selectedUser.id}`;

  const handleAssign = async () => {
    if (!selectedUser || !userId) return;

    if (selectedUser.id === userId) {
      setError(t("errorSameUser"));
      return;
    }

    if (isReplacing && !showReplaceConfirm) {
      setShowReplaceConfirm(true);
      return;
    }

    setShowReplaceConfirm(false);
    setIsLoading(true);
    setError(null);

    try {
      await referrerEndpoints.assignReferrer({
        refereeId: userId,
        referrerId: selectedUser.id,
      });
      notify.success(t("assignSuccess"));
      onSuccess?.();
    } catch (err: unknown) {
      console.error("Failed to assign referrer:", err);
      setError(getErrorMessage(err, t("errorGeneric")));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!userId) return;

    setIsLoading(true);
    setError(null);

    try {
      await referrerEndpoints.removeReferrer(userId);
      notify.success(t("removeSuccess"));
      onSuccess?.();
    } catch (err: unknown) {
      console.error("Failed to remove referrer:", err);
      setError(getErrorMessage(err, t("errorGeneric")));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle} style={styles.modalHeader}>
        <div style={styles.headerContent}>
          <i className="fas fa-user-friends" style={styles.headerIcon} />
          <span>{t("title")}</span>
        </div>
      </ModalHeader>

      <ModalBody style={styles.modalBody}>
        {/* User Info */}
        <div style={styles.userInfo}>
          <div style={styles.userName}>{displayName}</div>
        </div>

        {/* Current Referrer */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("currentReferrer")}</div>
          {referrer ? (
            <div style={styles.currentReferrerCard}>
              <div style={styles.currentReferrerInfo}>
                <span style={styles.currentReferrerName}>{referrer.name || referrer.email}</span>
                {referrer.name && referrer.email && <span style={styles.currentReferrerEmail}>{referrer.email}</span>}
              </div>
            </div>
          ) : (
            <div style={styles.noReferrer}>{t("noReferrer")}</div>
          )}
        </div>

        {/* Select New Referrer */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("selectReferrer")}</div>
          <UserSelect
            user={selectedUser}
            setUser={(user) => setSelectedUser(user && !Array.isArray(user) ? user : null)}
            isMulti={false}
            placeholder={t("selectPlaceholder")}
            isDisabled={isLoading}
          />
        </div>

        {/* Remove Referrer */}
        {referrer && (
          <div style={styles.section}>
            {!showRemoveConfirm ? (
              <Button color="danger" outline size="sm" onClick={() => setShowRemoveConfirm(true)} disabled={isLoading}>
                <i className="fas fa-unlink mr-2" aria-hidden="true" />
                {t("removeReferrer")}
              </Button>
            ) : (
              <div style={styles.removeConfirmCard}>
                <span style={styles.removeConfirmText}>{t("removeConfirm")}</span>
                <div style={styles.removeConfirmButtons}>
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    onClick={() => setShowRemoveConfirm(false)}
                    disabled={isLoading}
                  >
                    {t("cancel")}
                  </Button>
                  <Button color="danger" size="sm" onClick={handleRemove} disabled={isLoading}>
                    {isLoading ? <Spinner size="sm" /> : t("removeReferrer")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {showReplaceConfirm && referrer && selectedUser && (
          <div style={styles.section} role="alert">
            <div style={styles.removeConfirmCard}>
              <span style={styles.removeConfirmText}>
                {t("replaceConfirm", {
                  from: referrer.name || referrer.email,
                  to: selectedUser.name || selectedUser.email,
                })}
              </span>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={styles.errorBanner}>
            <i className="fas fa-exclamation-circle" style={styles.errorIcon} />
            <span>{error}</span>
          </div>
        )}
      </ModalBody>

      <ModalFooter style={styles.modalFooter}>
        <Button color="secondary" outline onClick={toggle} disabled={isLoading}>
          {t("cancel")}
        </Button>
        <Button color="primary" onClick={handleAssign} disabled={!selectedUser || isLoading} style={styles.saveButton}>
          {isLoading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {t("saving")}
            </>
          ) : (
            <>
              <i className="fas fa-save mr-2" aria-hidden="true" />
              {showReplaceConfirm ? t("confirmReplace") : t("save")}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const styles = {
  modalHeader: {
    borderBottom: "1px solid #e5e7eb",
    padding: "16px 20px",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  headerIcon: {
    color: "#6366f1",
    fontSize: 18,
  },
  modalBody: {
    padding: "20px",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: "1px solid #e5e7eb",
  },
  userName: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 10,
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  currentReferrerCard: {
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    padding: 14,
  },
  currentReferrerInfo: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 2,
  },
  currentReferrerName: {
    fontSize: 14,
    fontWeight: 500,
    color: "#111827",
  },
  currentReferrerEmail: {
    fontSize: 12,
    color: "#6b7280",
  },
  noReferrer: {
    fontSize: 13,
    color: "#9ca3af",
    fontStyle: "italic",
  },
  removeConfirmCard: {
    backgroundColor: "#fef2f2",
    borderRadius: 8,
    padding: 14,
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
  },
  removeConfirmText: {
    fontSize: 13,
    color: "#991b1b",
  },
  removeConfirmButtons: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    backgroundColor: "#fef2f2",
    borderRadius: 8,
    fontSize: 13,
    color: "#991b1b",
  },
  errorIcon: {
    color: "#ef4444",
  },
  modalFooter: {
    borderTop: "1px solid #e5e7eb",
    padding: "14px 20px",
    display: "flex",
    // A minWidth-pinned primary button plus its cancel overflowed a 360px
    // footer once the label was translated.
    flexWrap: "wrap" as const,
    justifyContent: "flex-end",
    gap: 10,
  },
  saveButton: {
    minWidth: 120,
  },
};

export default AssignReferrerModal;
